import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import emailMarketingService from '../lib/emailMarketingService';
import { 
  Mail, 
  Users, 
  BarChart3, 
  Settings, 
  Send, 
  Target, 
  Clock, 
  Zap, 
  TrendingUp, 
  Eye, 
  MousePointerClick, 
  UserMinus, 
  FileText, 
  Filter,
  Calendar,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Plus,
  Edit,
  Trash2,
  Play,
  Pause,
  RefreshCw,
  Star,
  Phone
} from 'lucide-react';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const EmailMarketing = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [showAutomation, setShowAutomation] = useState(false);
  
  // Real data state
  const [emailMarketingData, setEmailMarketingData] = useState({
    totalSubscribers: 0,
    openRate: 0,
    clickRate: 0,
    totalRevenue: 0,
    growthRate: 0,
    unsubscribeRate: 0,
    bounceRate: 0,
    totalCampaigns: 0,
    recentCampaigns: [],
    recentEvents: []
  });
  const [campaignPerformance, setCampaignPerformance] = useState([]);
  const [automationStatus, setAutomationStatus] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // User integration state
  const [userStats, setUserStats] = useState(null);
  const [userActivity, setUserActivity] = useState([]);
  const [duplicateEmails, setDuplicateEmails] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  
  // CRUD operation states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkAction, setBulkAction] = useState('');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'campaigns', label: 'Campaigns', icon: Send },
    { id: 'subscribers', label: 'Subscribers', icon: Users },
    { id: 'templates', label: 'Templates', icon: FileText },
    { id: 'automation', label: 'Automation', icon: Zap },
    { id: 'users', label: 'User Integration', icon: Target },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp },
  ];

  // Load all email marketing data
  const loadEmailMarketingData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [analytics, campaigns, automations, templates] = await Promise.all([
        emailMarketingService.getEmailMarketingAnalytics(),
        emailMarketingService.getCampaignPerformance(),
        emailMarketingService.getAutomationStatus(),
        emailMarketingService.getEmailTemplates()
      ]);

      setEmailMarketingData(analytics);
      setCampaignPerformance(campaigns);
      setAutomationStatus(automations);
      setEmailTemplates(templates);
    } catch (err) {
      console.error('Error loading email marketing data:', err);
      setError('Failed to load email marketing data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load subscribers data
  const loadSubscribers = async (page = 1, filters = {}) => {
    try {
      const result = await emailMarketingService.getSubscribers(page, 50, filters);
      setSubscribers(result.subscribers);
      return result;
    } catch (err) {
      console.error('Error loading subscribers:', err);
      setSubscribers([]);
      return { subscribers: [], total: 0, page: 1, totalPages: 0 };
    }
  };

  // Load user integration data
  const loadUserIntegrationData = async () => {
    try {
      const [stats, activity, duplicates] = await Promise.all([
        emailMarketingService.getUserStats(),
        emailMarketingService.getUserActivityTimeline(),
        emailMarketingService.checkDuplicateEmails()
      ]);
      
      setUserStats(stats);
      setUserActivity(activity);
      setDuplicateEmails(duplicates);
    } catch (err) {
      console.error('Error loading user integration data:', err);
    }
  };

  // Sync users to email marketing
  const syncUsersToEmailMarketing = async () => {
    setLoading(true);
    try {
      const result = await emailMarketingService.syncUsersToEmailMarketing();
      setSyncStatus(result);
      
      // Reload data after sync
      await loadEmailMarketingData();
      await loadSubscribers();
      
      return result;
    } catch (err) {
      console.error('Error syncing users:', err);
      setError('Failed to sync users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Merge duplicate subscribers
  const mergeDuplicateSubscribers = async (email) => {
    setLoading(true);
    try {
      const result = await emailMarketingService.mergeDuplicateSubscribers(email);
      
      if (result.success) {
        // Reload data after merge
        await loadEmailMarketingData();
        await loadSubscribers();
        await loadUserIntegrationData();
      }
      
      return result;
    } catch (err) {
      console.error('Error merging duplicates:', err);
      setError('Failed to merge duplicates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // CRUD Operations
  const handleCreate = async (type, data) => {
    setLoading(true);
    try {
      let result;
      switch (type) {
        case 'subscriber':
          result = await emailMarketingService.createSubscriber(data);
          break;
        case 'campaign':
          result = await emailMarketingService.createCampaign(data);
          break;
        case 'template':
          result = await emailMarketingService.createTemplate(data);
          break;
        case 'list':
          result = await emailMarketingService.createEmailList(data);
          break;
        case 'automation':
          result = await emailMarketingService.createAutomation(data);
          break;
        default:
          throw new Error('Invalid type for creation');
      }
      
      // Reload data after creation
      await loadEmailMarketingData();
      setShowCreateModal(false);
      return result;
    } catch (err) {
      console.error(`Error creating ${type}:`, err);
      setError(`Failed to create ${type}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (type, id, data) => {
    setLoading(true);
    try {
      let result;
      switch (type) {
        case 'subscriber':
          result = await emailMarketingService.updateSubscriber(id, data);
          break;
        case 'campaign':
          result = await emailMarketingService.updateCampaign(id, data);
          break;
        case 'template':
          result = await emailMarketingService.updateTemplate(id, data);
          break;
        case 'list':
          result = await emailMarketingService.updateEmailList(id, data);
          break;
        case 'automation':
          result = await emailMarketingService.updateAutomation(id, data);
          break;
        default:
          throw new Error('Invalid type for update');
      }
      
      // Reload data after update
      await loadEmailMarketingData();
      setShowEditModal(false);
      setEditingItem(null);
      return result;
    } catch (err) {
      console.error(`Error updating ${type}:`, err);
      setError(`Failed to update ${type}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type, id) => {
    if (!confirm(`Are you sure you want to delete this ${type}?`)) return;
    
    setLoading(true);
    try {
      let result;
      switch (type) {
        case 'subscriber':
          result = await emailMarketingService.deleteSubscriber(id);
          break;
        case 'campaign':
          result = await emailMarketingService.deleteCampaign(id);
          break;
        case 'template':
          result = await emailMarketingService.deleteTemplate(id);
          break;
        case 'list':
          result = await emailMarketingService.deleteEmailList(id);
          break;
        case 'automation':
          result = await emailMarketingService.deleteAutomation(id);
          break;
        default:
          throw new Error('Invalid type for deletion');
      }
      
      // Reload data after deletion
      await loadEmailMarketingData();
      return result;
    } catch (err) {
      console.error(`Error deleting ${type}:`, err);
      setError(`Failed to delete ${type}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedItems.length === 0) return;
    
    setLoading(true);
    try {
      switch (bulkAction) {
        case 'delete':
          await emailMarketingService.bulkDeleteSubscribers(selectedItems);
          break;
        case 'activate':
          await emailMarketingService.bulkUpdateSubscribers(
            selectedItems.map(id => ({ id, data: { status: 'active' } }))
          );
          break;
        case 'deactivate':
          await emailMarketingService.bulkUpdateSubscribers(
            selectedItems.map(id => ({ id, data: { status: 'inactive' } }))
          );
          break;
        default:
          throw new Error('Invalid bulk action');
      }
      
      // Reload data after bulk action
      await loadEmailMarketingData();
      setSelectedItems([]);
      setBulkAction('');
    } catch (err) {
      console.error('Error performing bulk action:', err);
      setError('Failed to perform bulk action. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadEmailMarketingData();
    loadUserIntegrationData();
  }, []);

  // Load data when switching to specific tabs
  useEffect(() => {
    if (activeTab === 'subscribers') {
      loadSubscribers();
    } else if (activeTab === 'users') {
      loadUserIntegrationData();
    }
  }, [activeTab]);

  // Load campaigns data
  const loadCampaigns = async (page = 1, filters = {}) => {
    try {
      const result = await emailMarketingService.getAllCampaigns(page, 20, filters);
      setCampaignPerformance(result.campaigns);
      return result;
    } catch (err) {
      console.error('Error loading campaigns:', err);
      setCampaignPerformance([]);
      return { campaigns: [], total: 0, page: 1, totalPages: 0 };
    }
  };

  // Use real data instead of mock data
  const stats = {
    totalSubscribers: emailMarketingData.totalSubscribers,
    openRate: emailMarketingData.openRate,
    clickRate: emailMarketingData.clickRate,
    unsubscribeRate: emailMarketingData.unsubscribeRate,
    revenue: emailMarketingData.totalRevenue,
    campaignsSent: emailMarketingData.totalCampaigns,
    activeAutomations: automationStatus.filter(auto => auto.status === 'active').length
  };

  // Use real campaign data instead of mock data
  const campaigns = campaignPerformance;

  // Use real automation data instead of mock data
  const automations = automationStatus;

  // Use real template data instead of mock data
  const templates = emailTemplates;

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold mx-auto"></div>
            <p className="mt-4 text-gray-400">Loading email marketing data...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
            <p className="text-red-400">{error}</p>
            <Button
              onClick={loadEmailMarketingData}
              className="ml-auto bg-red-600 hover:bg-red-700 text-white"
              size="sm"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {!loading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div variants={fadeIn}>
            <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-brand-gold/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Total Subscribers</p>
                    <p className="text-3xl font-bold text-white">{stats.totalSubscribers.toLocaleString()}</p>
                  </div>
                  <Users className="h-8 w-8 text-brand-gold" />
                </div>
                <div className={`mt-2 flex items-center text-sm ${
                  stats.totalSubscribers > 0 ? 'text-green-400' : 'text-gray-400'
                }`}>
                  <TrendingUp className="h-4 w-4 mr-1" />
                  {stats.totalSubscribers > 0 ? `+${emailMarketingData.growthRate}% this month` : 'No subscribers yet'}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={fadeIn}>
            <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-brand-gold/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Open Rate</p>
                    <p className="text-3xl font-bold text-white">{stats.openRate}%</p>
                  </div>
                  <Eye className="h-8 w-8 text-brand-gold" />
                </div>
                <div className="mt-2 flex items-center text-gray-400 text-sm">
                  <TrendingUp className="h-4 w-4 mr-1" />
                  Industry avg: 24.2%
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={fadeIn}>
            <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-brand-gold/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Click Rate</p>
                    <p className="text-3xl font-bold text-white">{stats.clickRate}%</p>
                  </div>
                  <MousePointerClick className="h-8 w-8 text-brand-gold" />
                </div>
                <div className="mt-2 flex items-center text-gray-400 text-sm">
                  <TrendingUp className="h-4 w-4 mr-1" />
                  Industry avg: 3.4%
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={fadeIn}>
            <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-brand-gold/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Revenue Generated</p>
                    <p className="text-3xl font-bold text-white">${stats.revenue.toLocaleString()}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-brand-gold" />
                </div>
                <div className="mt-2 flex items-center text-gray-400 text-sm">
                  <TrendingUp className="h-4 w-4 mr-1" />
                  {stats.campaignsSent} campaigns
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}

      {/* Quick Actions */}
      <motion.div variants={fadeIn}>
        <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-brand-gold/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <Zap className="h-6 w-6 mr-2 text-brand-gold" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button 
                onClick={() => setShowCreateCampaign(true)}
                className="bg-brand-gold text-brand-dark hover:bg-yellow-500 font-bold h-12"
              >
                <Send className="h-5 w-5 mr-2" />
                Create Campaign
              </Button>
              <Button 
                onClick={() => setShowCreateTemplate(true)}
                className="bg-blue-600 text-white hover:bg-blue-700 font-bold h-12"
              >
                <FileText className="h-5 w-5 mr-2" />
                New Template
              </Button>
              <Button 
                onClick={() => setShowAutomation(true)}
                className="bg-purple-600 text-white hover:bg-purple-700 font-bold h-12"
              >
                <Zap className="h-5 w-5 mr-2" />
                Setup Automation
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Recent Activity */}
      <motion.div variants={fadeIn}>
        <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-brand-gold/20">
          <CardHeader>
            <CardTitle className="text-white">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-400 mr-3" />
                  <span className="text-white">Welcome Series sent to 125 new subscribers</span>
                </div>
                <span className="text-gray-400 text-sm">2 hours ago</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-yellow-400 mr-3" />
                  <span className="text-white">Weekly Newsletter scheduled for tomorrow</span>
                </div>
                <span className="text-gray-400 text-sm">4 hours ago</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                <div className="flex items-center">
                  <TrendingUp className="h-5 w-5 text-blue-400 mr-3" />
                  <span className="text-white">Open rate increased by 2.1% this week</span>
                </div>
                <span className="text-gray-400 text-sm">1 day ago</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );

  const renderCampaigns = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Email Campaigns</h2>
        <div className="flex items-center space-x-3">
          <Button 
            onClick={loadEmailMarketingData}
            disabled={loading}
            className="bg-gray-600 hover:bg-gray-700 text-white"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            onClick={() => setShowCreateCampaign(true)}
            className="bg-brand-gold text-brand-dark hover:bg-yellow-500 font-bold"
          >
            <Plus className="h-5 w-5 mr-2" />
            New Campaign
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold mx-auto"></div>
            <p className="mt-4 text-gray-400">Loading campaigns...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
            <p className="text-red-400">{error}</p>
            <Button
              onClick={loadEmailMarketingData}
              className="ml-auto bg-red-600 hover:bg-red-700 text-white"
              size="sm"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        </div>
      )}

      {/* Campaigns List */}
      {!loading && !error && (
        <div className="grid grid-cols-1 gap-6">
          {campaigns.length > 0 ? (
            campaigns.map((campaign) => (
              <motion.div key={campaign.id} variants={fadeIn}>
                <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-brand-gold/20">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <h3 className="text-xl font-semibold text-white mr-3">{campaign.name}</h3>
                        <Badge 
                          className={
                            campaign.status === 'sent' ? 'bg-green-600' :
                            campaign.status === 'sending' ? 'bg-yellow-600' : 
                            campaign.status === 'draft' ? 'bg-gray-600' : 'bg-blue-600'
                          }
                        >
                          {campaign.status}
                        </Badge>
                      </div>
                      <div className="flex space-x-2">
                        <Button size="sm" variant="outline" className="border-gray-600 text-gray-300">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="border-gray-600 text-gray-300">
                          <Play className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="border-red-600 text-red-400">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-gray-400 text-sm">Sent</p>
                        <p className="text-lg font-semibold text-white">{(campaign.total_sent || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Open Rate</p>
                        <p className="text-lg font-semibold text-white">{campaign.open_rate || 0}%</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Click Rate</p>
                        <p className="text-lg font-semibold text-white">{campaign.click_rate || 0}%</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Revenue</p>
                        <p className="text-lg font-semibold text-white">${(campaign.revenue || 0).toLocaleString()}</p>
                      </div>
                    </div>
                    
                    <div className="mt-4 text-sm text-gray-400">
                      Created: {new Date(campaign.created_at).toLocaleDateString()}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))
          ) : (
            <div className="text-center py-12">
              <Mail className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Campaigns Found</h3>
              <p className="text-gray-400 mb-6">Create your first email campaign to get started</p>
              <Button 
                onClick={() => setShowCreateCampaign(true)}
                className="bg-brand-gold text-brand-dark hover:bg-yellow-500 font-bold"
              >
                <Plus className="h-5 w-5 mr-2" />
                Create First Campaign
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderSubscribers = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Subscriber Management</h2>
        <div className="flex space-x-2">
          <Button variant="outline" className="border-gray-600 text-gray-300">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
          <Button className="bg-brand-gold text-brand-dark hover:bg-yellow-500 font-bold">
            <Plus className="h-5 w-5 mr-2" />
            Add Subscriber
          </Button>
        </div>
      </div>

      <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-brand-gold/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <Input 
                placeholder="Search subscribers..." 
                className="placeholder-gray-400"
              />
              <Button variant="outline" className="border-gray-600 text-gray-300">
                Search
              </Button>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" className="border-gray-600 text-gray-300">
                Export
              </Button>
              <Button variant="outline" className="border-gray-600 text-gray-300">
                Import
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-gray-300">Email</th>
                  <th className="text-left py-3 px-4 text-gray-300">Name</th>
                  <th className="text-left py-3 px-4 text-gray-300">Status</th>
                  <th className="text-left py-3 px-4 text-gray-300">Subscribed</th>
                  <th className="text-left py-3 px-4 text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map((subscriber) => (
                  <tr key={subscriber.id} className="border-b border-gray-800">
                    <td className="py-3 px-4 text-white">{subscriber.email}</td>
                    <td className="py-3 px-4 text-gray-300">{subscriber.first_name} {subscriber.last_name}</td>
                    <td className="py-3 px-4">
                      <Badge className={
                        subscriber.status === 'active' ? 'bg-green-600' :
                        subscriber.status === 'unsubscribed' ? 'bg-red-600' :
                        subscriber.status === 'bounced' ? 'bg-yellow-600' : 'bg-gray-600'
                      }>
                        {subscriber.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-gray-300">
                      {new Date(subscriber.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex space-x-2">
                        <Button size="sm" variant="outline" className="border-gray-600 text-gray-300">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="border-red-600 text-red-400">
                          <UserMinus className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderTemplates = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Email Templates</h2>
        <Button 
          onClick={() => setShowCreateTemplate(true)}
          className="bg-brand-gold text-brand-dark hover:bg-yellow-500 font-bold"
        >
          <Plus className="h-5 w-5 mr-2" />
          New Template
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <motion.div key={template.id} variants={fadeIn}>
            <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-brand-gold/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">{template.name}</h3>
                  <Badge className="bg-blue-600">{template.category}</Badge>
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Last Used:</span>
                    <span className="text-white text-sm">
                      {template.last_used ? new Date(template.last_used).toLocaleDateString() : 'Never'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Usage Count:</span>
                    <span className="text-white text-sm">{template.usage_count || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Open Rate:</span>
                    <span className="text-white text-sm">{template.avg_open_rate || 0}%</span>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <Button size="sm" className="bg-brand-gold text-brand-dark hover:bg-yellow-500 flex-1">
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  <Button size="sm" variant="outline" className="border-gray-600 text-gray-300">
                    <Send className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-600 text-red-400">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const renderAutomation = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Email Automation</h2>
        <div className="flex items-center space-x-3">
          <Button 
            onClick={loadEmailMarketingData}
            disabled={loading}
            className="bg-gray-600 hover:bg-gray-700 text-white"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            onClick={() => setShowAutomation(true)}
            className="bg-brand-gold text-brand-dark hover:bg-yellow-500 font-bold"
          >
            <Plus className="h-5 w-5 mr-2" />
            New Automation
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold mx-auto"></div>
            <p className="mt-4 text-gray-400">Loading automations...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
            <p className="text-red-400">{error}</p>
            <Button
              onClick={loadEmailMarketingData}
              className="ml-auto bg-red-600 hover:bg-red-700 text-white"
              size="sm"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        </div>
      )}

      {/* Automations List */}
      {!loading && !error && (
        <div className="grid grid-cols-1 gap-6">
          {automations.length > 0 ? (
            automations.map((automation) => (
              <motion.div key={automation.id} variants={fadeIn}>
                <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-brand-gold/20">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <h3 className="text-xl font-semibold text-white mr-3">{automation.name}</h3>
                        <Badge 
                          className={
                            automation.status === 'active' ? 'bg-green-600' : 
                            automation.status === 'paused' ? 'bg-yellow-600' : 'bg-red-600'
                          }
                        >
                          {automation.status}
                        </Badge>
                      </div>
                      <div className="flex space-x-2">
                        <Button size="sm" variant="outline" className="border-gray-600 text-gray-300">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="border-gray-600 text-gray-300">
                          {automation.status === 'active' ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        </Button>
                        <Button size="sm" variant="outline" className="border-red-600 text-red-400">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-gray-400 text-sm">Trigger</p>
                        <p className="text-lg font-semibold text-white">{automation.trigger_type || 'Unknown'}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Triggered</p>
                        <p className="text-lg font-semibold text-white">{(automation.total_triggered || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Emails Sent</p>
                        <p className="text-lg font-semibold text-white">{(automation.total_sent || 0).toLocaleString()}</p>
                      </div>
                    </div>
                    
                    <div className="mt-4 text-sm text-gray-400">
                      Created: {new Date(automation.created_at).toLocaleDateString()}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))
          ) : (
            <div className="text-center py-12">
              <Zap className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Automations Found</h3>
              <p className="text-gray-400 mb-6">Set up your first email automation workflow</p>
              <Button 
                onClick={() => setShowAutomation(true)}
                className="bg-brand-gold text-brand-dark hover:bg-yellow-500 font-bold"
              >
                <Plus className="h-5 w-5 mr-2" />
                Create First Automation
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderUserIntegration = () => (
    <div className="space-y-6">
      {/* User Integration Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">User Integration</h2>
          <p className="text-gray-400">Manage users from bookings and reviews</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={syncUsersToEmailMarketing}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Sync Users
          </Button>
        </div>
      </div>

      {/* Sync Status */}
      {syncStatus && (
        <Card className="bg-brand-charcoal border-brand-gold/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-400" />
              <h3 className="text-lg font-semibold text-white">Sync Complete</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">{syncStatus.successCount}</div>
                <div className="text-sm text-gray-400">Users Synced</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">{syncStatus.totalProcessed}</div>
                <div className="text-sm text-gray-400">Total Processed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-400">{syncStatus.errorCount}</div>
                <div className="text-sm text-gray-400">Errors</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Statistics */}
      {userStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card className="bg-brand-charcoal border-brand-gold/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-400">Total Users</p>
                  <p className="text-2xl font-bold text-white">{userStats.totalUsers}</p>
                </div>
                <Users className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-brand-charcoal border-brand-gold/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-400">Booking Users</p>
                  <p className="text-2xl font-bold text-white">{userStats.bookingUsers}</p>
                </div>
                <Calendar className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-brand-charcoal border-brand-gold/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-400">Review Users</p>
                  <p className="text-2xl font-bold text-white">{userStats.reviewUsers}</p>
                </div>
                <Star className="w-8 h-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-brand-charcoal border-brand-gold/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-400">Both Sources</p>
                  <p className="text-2xl font-bold text-white">{userStats.bothUsers}</p>
                </div>
                <Target className="w-8 h-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-brand-charcoal border-brand-gold/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-400">With Phone</p>
                  <p className="text-2xl font-bold text-white">{userStats.usersWithPhone}</p>
                </div>
                <Phone className="w-8 h-8 text-orange-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-brand-charcoal border-brand-gold/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-400">Recent Users</p>
                  <p className="text-2xl font-bold text-white">{userStats.recentUsers}</p>
                </div>
                <Clock className="w-8 h-8 text-cyan-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Duplicate Emails */}
      {duplicateEmails.length > 0 && (
        <Card className="bg-brand-charcoal border-brand-gold/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-400" />
              Duplicate Emails Found
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {duplicateEmails.map((duplicate, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-yellow-900/20 rounded-lg">
                  <div>
                    <p className="font-medium text-white">{duplicate.email}</p>
                    <p className="text-sm text-gray-400">{duplicate.count} duplicate(s)</p>
                  </div>
                  <Button
                    onClick={() => mergeDuplicateSubscribers(duplicate.email)}
                    disabled={loading}
                    className="bg-yellow-600 hover:bg-yellow-700"
                  >
                    Merge
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Activity Timeline */}
      {userActivity.length > 0 && (
        <Card className="bg-brand-charcoal border-brand-gold/20">
          <CardHeader>
            <CardTitle className="text-white">Recent User Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {userActivity.slice(0, 10).map((activity, index) => (
                <div key={index} className="flex items-center gap-4 p-3 bg-gray-800/50 rounded-lg">
                  <div className={`w-2 h-2 rounded-full ${
                    activity.type === 'booking' ? 'bg-green-400' : 'bg-blue-400'
                  }`} />
                  <div className="flex-1">
                    <p className="text-white font-medium">{activity.user}</p>
                    <p className="text-sm text-gray-400">
                      {activity.type === 'booking' ? 'Made a booking' : 'Left a review'} - {activity.email}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(activity.date).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge variant={activity.type === 'booking' ? 'default' : 'secondary'}>
                    {activity.type}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderAnalytics = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white">Email Analytics</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div variants={fadeIn}>
          <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-brand-gold/20">
            <CardHeader>
              <CardTitle className="text-white">Performance Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Open Rate</span>
                  <span className="text-white font-semibold">24.8%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Click Rate</span>
                  <span className="text-white font-semibold">3.2%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Unsubscribe Rate</span>
                  <span className="text-white font-semibold">0.8%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Bounce Rate</span>
                  <span className="text-white font-semibold">2.1%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div variants={fadeIn}>
          <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-brand-gold/20">
            <CardHeader>
              <CardTitle className="text-white">Revenue Tracking</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">This Month</span>
                  <span className="text-white font-semibold">$12,450</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Last Month</span>
                  <span className="text-white font-semibold">$10,230</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Growth</span>
                  <span className="text-green-400 font-semibold">+21.7%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">ROI</span>
                  <span className="text-white font-semibold">340%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <motion.div variants={fadeIn}>
        <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-brand-gold/20">
          <CardHeader>
            <CardTitle className="text-white">Campaign Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4 text-gray-300">Campaign</th>
                    <th className="text-left py-3 px-4 text-gray-300">Sent</th>
                    <th className="text-left py-3 px-4 text-gray-300">Opens</th>
                    <th className="text-left py-3 px-4 text-gray-300">Clicks</th>
                    <th className="text-left py-3 px-4 text-gray-300">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((campaign) => (
                    <tr key={campaign.id} className="border-b border-gray-800">
                      <td className="py-3 px-4 text-white">{campaign.name}</td>
                      <td className="py-3 px-4 text-gray-300">{(campaign.total_sent || 0).toLocaleString()}</td>
                      <td className="py-3 px-4 text-gray-300">{campaign.open_rate || 0}%</td>
                      <td className="py-3 px-4 text-gray-300">{campaign.click_rate || 0}%</td>
                      <td className="py-3 px-4 text-gray-300">${(campaign.revenue || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Email Marketing - Akiblackcar</title>
        <meta name="description" content="Comprehensive email marketing solution with automation, campaigns, templates, and analytics for Akiblackcar." />
      </Helmet>
      
      <motion.div 
        initial="hidden" 
        animate="visible" 
        variants={fadeIn} 
        className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16"
      >
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div variants={fadeIn}>
            <h1 className="text-4xl md:text-5xl font-bold font-serif text-brand-gold mb-4">
              Email Marketing Platform
            </h1>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Comprehensive email marketing solution with automation, campaigns, templates, and detailed analytics to grow your business.
            </p>
          </motion.div>
        </div>

        {/* Navigation Tabs */}
        <motion.div variants={fadeIn}>
          <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-brand-gold/20 mb-8">
            <CardContent className="p-2">
              <div className="flex flex-wrap gap-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <Button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 md:flex-none ${
                        activeTab === tab.id
                          ? 'bg-brand-gold text-brand-dark hover:bg-yellow-500'
                          : 'bg-transparent text-gray-300 hover:bg-gray-700 hover:text-white'
                      }`}
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      {tab.label}
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Tab Content */}
        <motion.div variants={fadeIn}>
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'campaigns' && renderCampaigns()}
          {activeTab === 'subscribers' && renderSubscribers()}
          {activeTab === 'templates' && renderTemplates()}
          {activeTab === 'automation' && renderAutomation()}
          {activeTab === 'users' && renderUserIntegration()}
          {activeTab === 'analytics' && renderAnalytics()}
        </motion.div>
      </motion.div>
    </>
  );
};

export default EmailMarketing;
